import type { Subscription as SchemaSubscription } from '@vote4dance/judging-schema';
import type { UserId } from '@vote4dance/judging-types';
import { StreamSource } from "../hooks/streams";

export type SubscriptionVendor = 'ios' | 'android' | 'stripe';
export type SubscriptionStatus = 'active' | 'inactive' | 'canceled' | 'expired';
export type SubscriptionRenewMethod = 'create-checkout-session' | 'validate-native';
export type NativeSubscriptionVendor = Exclude<SubscriptionVendor, 'stripe'>;
export type SubscriptionErrorCode =
    | 'session-required'
    | 'missing-receipt'
    | 'missing-vendor'
    | 'invalid-vendor'
    | 'invalid-receipt'
    | 'invalid-product-id'
    | 'invalid-purchase-id'
    | 'invalid-package-name'
    | 'invalid-purchase-token'
    | 'invalid-status'
    | 'not-entitled'
    | 'not-renewable'
    | 'subscription-not-found'
    | 'validation-failed'
    | 'welcome-gift-unavailable'
    | 'already-has-entitlement'
    | 'internal-error';

export interface SubscriptionMetadata {
    eventType?: string;
    invoice?: string | null;
    stripe_status?: string;
    welcomeGift?: boolean;
    durationDays?: number;
    source?: string;
    vendor?: SubscriptionVendor;
    productId?: string | null;
    inputProductId?: string | null;
    inputPurchaseId?: string | null;
    receiptType?: string;
    receiptLength?: number;
    notificationType?: string | null;
    notificationSubtype?: string | null;
    notificationUUID?: string | null;
    transactionId?: string | null;
    originalTransactionId?: string | null;
    subscriptionId?: string;
    startsAt?: string;
    endsAt?: string;
    environment?: string | null;
    validationEnvironment?: string | null;
    signedPayloadLength?: number | null;
    autoRenewStatus?: string | number | boolean | null;
    billingRetry?: boolean;
    validatedAt?: string;
}

export type SubscriptionMembership = Omit<SchemaSubscription, 'vendor' | 'status' | 'metadata' | 'starts_at' | 'ends_at' | 'created_at' | 'updated_at'> & {
    vendor: SubscriptionVendor;
    status: SubscriptionStatus;
    raw_status?: SubscriptionStatus;
    metadata?: string | SubscriptionMetadata | null;
    has_entitlement: boolean;
    can_renew: boolean;
    renew_method: SubscriptionRenewMethod;
    starts_at?: string;
    ends_at?: string;
    created_at?: string;
    updated_at?: string;
};

export interface ValidateNativeSubscriptionRequest {
    vendor: NativeSubscriptionVendor;
    receipt: string | Record<string, unknown> | number[] | Uint8Array;
    product_id?: string;
    productId?: string;
    purchase_id?: string;
    purchaseId?: string;
    packageName?: string;
    package_name?: string;
    purchaseToken?: string;
    purchase_token?: string;
}

export function parseSubscriptionMetadata(metadata: SubscriptionMembership['metadata']): SubscriptionMetadata | null {
    if (!metadata) {
        return null;
    }
    if (typeof metadata === 'string') {
        try {
            const parsed: unknown = JSON.parse(metadata);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? parsed as SubscriptionMetadata
                : null;
        } catch {
            return null;
        }
    }
    return metadata;
}

export function SubscriptionSource(user_id: UserId) {
    return new StreamSource<SubscriptionMembership | null>(`/subscriptions/${user_id}`);
}

export const AdminSubscriptionSource = new StreamSource<SubscriptionMembership[]>("/subscriptions");